import { useState, useEffect, useCallback } from 'react';
import { useLocation } from 'wouter';
import { useAuthStore } from '../hooks/useAuth';
import toast from 'react-hot-toast';
import { Shield, Building2, Search, Clock, CheckCircle, AlertTriangle, X, MapPin, Phone, Mail, Globe, Anchor, CalendarDays, Activity, Plus, History, MessageSquare, ShieldOff, ShieldCheck, RotateCcw, Rocket, Send, Copy, Ban } from 'lucide-react';


interface WarehouseInvitationRow {
    id: string;
    invitedEmail: string;
    companyName: string | null;
    invitedByEmail: string;
    expiresAt: string | null;
    usedAt: string | null;
    usedByUserId: string | null;
    usedWarehouseId: string | null;
    revokedAt: string | null;
    createdAt: string | null;
    status: 'pending' | 'used' | 'expired' | 'revoked';
}

interface TrialRequestApplicant {
    id: string;
    companyName: string;
    contactName: string;
    email: string;
    city: string | null;
    province: string | null;
    dockDoors: number | null;
    message: string | null;
    createdAt: string | null;
}

interface GeneratedInvitationResult {
    invitation: WarehouseInvitationRow;
    inviteToken: string;
    inviteUrl: string;
    emailSent?: boolean;
}



interface WarehouseRow {
    warehouseId: string;
    warehouseName: string;
    ownerEmail: string;
    createdAt: string | null;
    trialStartAt: string | null;
    trialEndAt: string | null;
    trialDaysRemaining: number | null;
    subscriptionStatus: string | null;
    plan: string | null;
    isActive: boolean;
}

type FilterTab = 'all' | 'trialing' | 'expired' | 'active';

interface WarehouseDetail {
    warehouseId: string;
    warehouseName: string;
    ownerEmail: string | null;
    ownerName: string | null;
    address: string | null;
    phone: string | null;
    warehouseEmail: string | null;
    timeZone: string | null;
    dockCount: number;
    appointmentCount: number;
    lastActivity: string | null;
    createdAt: string | null;
    trialStartAt: string | null;
    trialEndAt: string | null;
    trialDaysRemaining: number | null;
    subscriptionStatus: string | null;
    plan: string | null;
    isActive: boolean;
}

interface AuditEntry {
    id: string;
    action: string;
    adminEmail: string;
    oldValue: string | null;
    newValue: string | null;
    reason: string | null;
    createdAt: string | null;
}

export default function AdminAccounts() {
    const { user, token } = useAuthStore();
    const [, setLocation] = useLocation();
    const [warehouses, setWarehouses] = useState<WarehouseRow[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [filter, setFilter] = useState<FilterTab>('all');
    const [search, setSearch] = useState('');
    const [selectedId, setSelectedId] = useState<string | null>(null);
    const [detail, setDetail] = useState<WarehouseDetail | null>(null);
    const [detailLoading, setDetailLoading] = useState(false);
    const [extending, setExtending] = useState(false);
    const [saving, setSaving] = useState(false);
    const [pendingStatus, setPendingStatus] = useState('');
    const [pendingPlan, setPendingPlan] = useState('');
    const [auditHistory, setAuditHistory] = useState<AuditEntry[]>([]);
    const [noteText, setNoteText] = useState('');
    const [addingNote, setAddingNote] = useState(false);
    const [toggling, setToggling] = useState(false);
    const [confirmDeactivate, setConfirmDeactivate] = useState(false);
    const [resetting, setResetting] = useState(false);
    const [confirmReset, setConfirmReset] = useState(false);
    const [markingOnboarding, setMarkingOnboarding] = useState(false);

    // Warehouse Trial Invitations state
    const [invitations, setInvitations] = useState<WarehouseInvitationRow[]>([]);
    const [trialRequests, setTrialRequests] = useState<TrialRequestApplicant[]>([]);
    const [invitationsLoading, setInvitationsLoading] = useState(false);
    const [inviteEmail, setInviteEmail] = useState('');
    const [inviteCompany, setInviteCompany] = useState('');
    const [inviteExpiryDays, setInviteExpiryDays] = useState(14);
    const [generatingInvite, setGeneratingInvite] = useState(false);
    const [generatedInvite, setGeneratedInvite] = useState<GeneratedInvitationResult | null>(null);
    const [sendingInviteId, setSendingInviteId] = useState<string | null>(null);
    const [revokingInviteId, setRevokingInviteId] = useState<string | null>(null);

    const fetchInvitations = useCallback(async () => {
        if (!user?.isAdmin || !token) return;
        setInvitationsLoading(true);
        try {
            const res = await fetch('/api/admin/warehouse-invitations', {
                headers: { Authorization: `Bearer ${token}` },
            });
            if (res.ok) {
                const data = await res.json();
                setInvitations(data.invitations || []);
                setTrialRequests(data.trialRequests || []);
            }
        } catch {
            /* silent */
        } finally {
            setInvitationsLoading(false);
        }
    }, [user, token]);

    // Guard: redirect non-admin users
    useEffect(() => {
        if (!user?.isAdmin) {
            setLocation(user?.role === 'carrier' ? '/carrier-dashboard' : '/dashboard');
        }
    }, [user, setLocation]);

    // Fetch warehouses & invitations
    useEffect(() => {
        if (!user?.isAdmin) return;

        const fetchWarehouses = async () => {
            try {
                setLoading(true);
                const res = await fetch('/api/admin/warehouses', {
                    headers: { Authorization: `Bearer ${token}` },
                });
                if (res.status === 403 || res.status === 401) {
                    setError('Access denied');
                    return;
                }
                if (!res.ok) {
                    setError('Failed to load warehouses');
                    return;
                }
                const data = await res.json();
                setWarehouses(data.warehouses || []);
            } catch {
                setError('Network error');
            } finally {
                setLoading(false);
            }
        };

        fetchWarehouses();
        fetchInvitations();
    }, [user, token, fetchInvitations]);

    const handleSelectApplicant = (applicantId: string) => {
        const found = trialRequests.find((r) => r.id === applicantId);
        if (found) {
            setInviteEmail(found.email);
            setInviteCompany(found.companyName || '');
        }
    };

    const handleGenerateInvitation = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!inviteEmail.trim() || generatingInvite) return;
        setGeneratingInvite(true);
        try {
            const res = await fetch('/api/admin/warehouse-invitations', {
                method: 'POST',
                headers: {
                    Authorization: `Bearer ${token}`,
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    email: inviteEmail.trim(),
                    ...(inviteCompany.trim() ? { companyName: inviteCompany.trim() } : {}),
                    expiresInDays: inviteExpiryDays,
                }),
            });
            const data = await res.json().catch(() => ({}));
            if (!res.ok) {
                toast.error(data.error || 'Failed to generate invitation');
                return;
            }
            setGeneratedInvite({
                invitation: data.invitation,
                inviteToken: data.inviteToken,
                inviteUrl: data.inviteUrl,
                emailSent: false,
            });
            setInviteEmail('');
            setInviteCompany('');
            toast.success('Single-use invitation generated. Review details below.');
            fetchInvitations();
        } catch {
            toast.error('Network error while generating invitation');
        } finally {
            setGeneratingInvite(false);
        }
    };

    const handleSendInvitationEmail = async (invitationId: string, inviteToken?: string) => {
        if (sendingInviteId) return;
        setSendingInviteId(invitationId);
        try {
            const res = await fetch(`/api/admin/warehouse-invitations/${invitationId}/send-email`, {
                method: 'POST',
                headers: {
                    Authorization: `Bearer ${token}`,
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(inviteToken ? { inviteToken } : {}),
            });
            const data = await res.json().catch(() => ({}));
            if (!res.ok) {
                toast.error(data.error || 'Failed to send invitation via Postmark');
                return;
            }
            toast.success(`Invitation email sent via Postmark to ${data.sentTo}`);
            if (generatedInvite && generatedInvite.invitation.id === invitationId) {
                setGeneratedInvite({
                    ...generatedInvite,
                    inviteUrl: data.inviteUrl || generatedInvite.inviteUrl,
                    emailSent: true,
                });
            }
            fetchInvitations();
        } catch {
            toast.error('Network error while sending invitation email');
        } finally {
            setSendingInviteId(null);
        }
    };

    const handleRevokeInvitation = async (invitationId: string) => {
        if (revokingInviteId) return;
        setRevokingInviteId(invitationId);
        try {
            const res = await fetch(`/api/admin/warehouse-invitations/${invitationId}/revoke`, {
                method: 'POST',
                headers: {
                    Authorization: `Bearer ${token}`,
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ reason: 'Revoked by founder in Admin console' }),
            });
            const data = await res.json().catch(() => ({}));
            if (!res.ok) {
                toast.error(data.error || 'Failed to revoke invitation');
                return;
            }
            toast.success('Invitation revoked');
            if (generatedInvite && generatedInvite.invitation.id === invitationId) {
                setGeneratedInvite(null);
            }
            fetchInvitations();
        } catch {
            toast.error('Network error while revoking invitation');
        } finally {
            setRevokingInviteId(null);
        }
    };

    // Fetch detail when row selected
    const openDetail = useCallback(async (id: string) => {
        setSelectedId(id);
        setDetail(null);
        setDetailLoading(true);
        try {
            const res = await fetch(`/api/admin/warehouses/${id}`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            if (res.ok) {
                const data = await res.json();
                setDetail(data.warehouse);
                setPendingStatus(data.warehouse.subscriptionStatus || '');
                setPendingPlan(data.warehouse.plan || '');
            }
        } catch { /* silent */ } finally {
            setDetailLoading(false);
        }
        // Fetch audit history in parallel
        try {
            const auditRes = await fetch(`/api/admin/warehouses/${id}/audit-history`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            if (auditRes.ok) {
                const auditData = await auditRes.json();
                setAuditHistory(auditData.entries || []);
            }
        } catch { /* silent */ }
    }, [token]);

    const closeDetail = useCallback(() => {
        setSelectedId(null);
        setDetail(null);
    }, []);

    // Escape key to close
    useEffect(() => {
        const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') closeDetail(); };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [closeDetail]);

    // Extend trial
    const extendTrial = useCallback(async (days: number) => {
        if (!selectedId || extending) return;
        setExtending(true);
        try {
            const res = await fetch(`/api/admin/warehouses/${selectedId}/extend-trial`, {
                method: 'POST',
                headers: {
                    Authorization: `Bearer ${token}`,
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ days }),
            });
            if (!res.ok) {
                const data = await res.json().catch(() => ({}));
                toast.error(data.error || 'Extension failed');
                return;
            }
            await res.json();
            toast.success(`Trial extended +${days} days`);
            // Refresh detail
            openDetail(selectedId);
            // Refresh list
            const listRes = await fetch('/api/admin/warehouses', {
                headers: { Authorization: `Bearer ${token}` },
            });
            if (listRes.ok) {
                const listData = await listRes.json();
                setWarehouses(listData.warehouses || []);
            }
        } catch {
            toast.error('Network error');
        } finally {
            setExtending(false);
        }
    }, [selectedId, extending, token, openDetail]);

    // Update subscription
    const STATUSES = ['trialing', 'active', 'expired', 'past_due', 'canceled'];
    const PLANS = ['free_trial', 'starter', 'operations', 'professional', 'enterprise'];

    const updateSubscription = useCallback(async () => {
        if (!selectedId || saving) return;
        const body: Record<string, string> = {};
        if (pendingStatus && pendingStatus !== detail?.subscriptionStatus) body.subscriptionStatus = pendingStatus;
        if (pendingPlan && pendingPlan !== detail?.plan) body.plan = pendingPlan;
        if (Object.keys(body).length === 0) {
            toast('No changes to save', { icon: 'ℹ️' });
            return;
        }
        setSaving(true);
        try {
            const res = await fetch(`/api/admin/warehouses/${selectedId}/update-subscription`, {
                method: 'POST',
                headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
                body: JSON.stringify(body),
            });
            if (!res.ok) {
                const data = await res.json().catch(() => ({}));
                toast.error(data.error || 'Update failed');
                return;
            }
            toast.success('Subscription updated');
            openDetail(selectedId);
            const listRes = await fetch('/api/admin/warehouses', { headers: { Authorization: `Bearer ${token}` } });
            if (listRes.ok) {
                const listData = await listRes.json();
                setWarehouses(listData.warehouses || []);
            }
        } catch {
            toast.error('Network error');
        } finally {
            setSaving(false);
        }
    }, [selectedId, saving, pendingStatus, pendingPlan, detail, token, openDetail]);

    // Add note
    const addNote = useCallback(async () => {
        if (!selectedId || addingNote || !noteText.trim()) return;
        setAddingNote(true);
        try {
            const res = await fetch(`/api/admin/warehouses/${selectedId}/note`, {
                method: 'POST',
                headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
                body: JSON.stringify({ note: noteText.trim() }),
            });
            if (!res.ok) {
                const data = await res.json().catch(() => ({}));
                toast.error(data.error || 'Failed to add note');
                return;
            }
            toast.success('Note added');
            setNoteText('');
            // Refresh audit timeline
            const auditRes = await fetch(`/api/admin/warehouses/${selectedId}/audit-history`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            if (auditRes.ok) {
                const auditData = await auditRes.json();
                setAuditHistory(auditData.entries || []);
            }
        } catch {
            toast.error('Network error');
        } finally {
            setAddingNote(false);
        }
    }, [selectedId, addingNote, noteText, token]);

    // Toggle access
    const toggleAccess = useCallback(async (active: boolean) => {
        if (!selectedId || toggling) return;
        setToggling(true);
        setConfirmDeactivate(false);
        try {
            const res = await fetch(`/api/admin/warehouses/${selectedId}/access`, {
                method: 'POST',
                headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
                body: JSON.stringify({ active }),
            });
            if (!res.ok) {
                const data = await res.json().catch(() => ({}));
                toast.error(data.error || 'Failed to update access');
                return;
            }
            toast.success(active ? 'Access reactivated' : 'Access deactivated');
            openDetail(selectedId);
            const listRes = await fetch('/api/admin/warehouses', { headers: { Authorization: `Bearer ${token}` } });
            if (listRes.ok) {
                const listData = await listRes.json();
                setWarehouses(listData.warehouses || []);
            }
        } catch {
            toast.error('Network error');
        } finally {
            setToggling(false);
        }
    }, [selectedId, toggling, token, openDetail]);

    // Reset trial
    const resetTrial = useCallback(async () => {
        if (!selectedId || resetting) return;
        setResetting(true);
        setConfirmReset(false);
        try {
            const res = await fetch(`/api/admin/warehouses/${selectedId}/reset-trial`, {
                method: 'POST',
                headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
                body: JSON.stringify({}),
            });
            if (!res.ok) {
                const data = await res.json().catch(() => ({}));
                toast.error(data.error || 'Reset failed');
                return;
            }
            toast.success('Trial reset — fresh 30 days');
            openDetail(selectedId);
            const listRes = await fetch('/api/admin/warehouses', { headers: { Authorization: `Bearer ${token}` } });
            if (listRes.ok) {
                const listData = await listRes.json();
                setWarehouses(listData.warehouses || []);
            }
        } catch {
            toast.error('Network error');
        } finally {
            setResetting(false);
        }
    }, [selectedId, resetting, token, openDetail]);

    // Mark onboarding complete
    const markOnboardingComplete = useCallback(async () => {
        if (!selectedId || markingOnboarding) return;
        setMarkingOnboarding(true);
        try {
            const res = await fetch(`/api/admin/warehouses/${selectedId}/onboarding-complete`, {
                method: 'POST',
                headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
                body: JSON.stringify({}),
            });
            if (!res.ok) {
                const data = await res.json().catch(() => ({}));
                toast.error(data.error || 'Failed to mark onboarding complete');
                return;
            }
            const data = await res.json();
            const msg = data.trialExtended ? 'Onboarding complete — trial extended to 60 days' : 'Onboarding complete';
            toast.success(msg);
            openDetail(selectedId);
            const listRes = await fetch('/api/admin/warehouses', { headers: { Authorization: `Bearer ${token}` } });
            if (listRes.ok) {
                const listData = await listRes.json();
                setWarehouses(listData.warehouses || []);
            }
        } catch {
            toast.error('Network error');
        } finally {
            setMarkingOnboarding(false);
        }
    }, [selectedId, markingOnboarding, token, openDetail]);

    // Don't render for non-admin
    if (!user?.isAdmin) {
        return <></>;
    }

    // Counts
    const counts = {
        all: warehouses.length,
        trialing: warehouses.filter(w => w.subscriptionStatus === 'trialing').length,
        expired: warehouses.filter(w => w.subscriptionStatus === 'expired').length,
        active: warehouses.filter(w => w.subscriptionStatus === 'active').length,
    };

    // Filter + search
    const filtered = warehouses
        .filter(w => {
            if (filter === 'all') return true;
            return w.subscriptionStatus === filter;
        })
        .filter(w => {
            if (!search) return true;
            const q = search.toLowerCase();
            return (
                w.warehouseName?.toLowerCase().includes(q) ||
                w.ownerEmail?.toLowerCase().includes(q)
            );
        })
        // Sort: expired first, then by days remaining ascending, then newest
        .sort((a, b) => {
            const aDays = a.trialDaysRemaining ?? 999;
            const bDays = b.trialDaysRemaining ?? 999;
            return aDays - bDays;
        });

    const formatDate = (iso: string | null) => {
        if (!iso) return '—';
        return new Date(iso).toLocaleDateString('en-US', {
            month: 'short', day: 'numeric', year: 'numeric',
        });
    };

    const statusBadge = (status: string | null, daysLeft: number | null, isActive?: boolean) => {
        if (isActive === false) {
            return (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold bg-red-500/15 text-red-300 border border-red-500/25">
                    <ShieldOff size={11} /> Deactivated
                </span>
            );
        }
        if (status === 'expired' || (daysLeft !== null && daysLeft <= 0)) {
            return (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold bg-red-500/15 text-red-400 border border-red-500/20">
                    <AlertTriangle size={11} /> Expired
                </span>
            );
        }
        if (status === 'active') {
            return (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold bg-emerald-500/15 text-emerald-400 border border-emerald-500/20">
                    <CheckCircle size={11} /> Active
                </span>
            );
        }
        // trialing
        const urgent = daysLeft !== null && daysLeft <= 5;
        return (
            <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold border ${urgent
                ? 'bg-amber-500/15 text-amber-400 border-amber-500/20'
                : 'bg-indigo-500/15 text-indigo-400 border-indigo-500/20'
                }`}>
                <Clock size={11} /> Trialing
            </span>
        );
    };

    const daysLeftDisplay = (days: number | null) => {
        if (days === null) return '—';
        if (days <= 0) return <span className="text-red-400 font-semibold">Expired</span>;
        if (days <= 5) return <span className="text-amber-400 font-semibold">{days}d</span>;
        return <span className="text-slate-300">{days}d</span>;
    };

    const filterTabs: { key: FilterTab; label: string; count: number }[] = [
        { key: 'all', label: 'All', count: counts.all },
        { key: 'trialing', label: 'Trialing', count: counts.trialing },
        { key: 'expired', label: 'Expired', count: counts.expired },
        { key: 'active', label: 'Active', count: counts.active },
    ];

    return (
        <div className="p-6 max-w-[1400px] mx-auto space-y-6">
            {/* Header */}
            <div className="flex items-center gap-3">
                <div className="p-2.5 bg-indigo-500/10 rounded-xl border border-indigo-500/20">
                    <Shield size={22} className="text-indigo-400" />
                </div>
                <div>
                    <h1 className="text-2xl font-bold text-white tracking-tight">Admin — Accounts</h1>
                    <p className="text-sm text-slate-400">Internal warehouse account overview</p>
                </div>
            </div>

            {/* Summary Cards */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {[
                    { label: 'Total', value: counts.all, color: 'slate', icon: Building2 },
                    { label: 'Trialing', value: counts.trialing, color: 'indigo', icon: Clock },
                    { label: 'Active', value: counts.active, color: 'emerald', icon: CheckCircle },
                    { label: 'Expired', value: counts.expired, color: 'red', icon: AlertTriangle },
                ].map(card => (
                    <div
                        key={card.label}
                        className={`rounded-xl border p-4 bg-${card.color}-500/5 border-${card.color}-500/15`}
                        style={{
                            background: `linear-gradient(135deg, rgba(var(--${card.color}), 0.05), transparent)`,
                            borderColor: card.color === 'slate' ? 'rgba(100,116,139,0.15)' :
                                card.color === 'indigo' ? 'rgba(99,102,241,0.15)' :
                                    card.color === 'emerald' ? 'rgba(16,185,129,0.15)' :
                                        'rgba(239,68,68,0.15)',
                            backgroundColor: card.color === 'slate' ? 'rgba(100,116,139,0.05)' :
                                card.color === 'indigo' ? 'rgba(99,102,241,0.05)' :
                                    card.color === 'emerald' ? 'rgba(16,185,129,0.05)' :
                                        'rgba(239,68,68,0.05)',
                        }}
                    >
                        <div className="flex items-center gap-2 mb-1">
                            <card.icon size={14} className="text-slate-400" />
                            <span className="text-[11px] text-slate-400 uppercase tracking-wider font-medium">{card.label}</span>
                        </div>
                        <p className="text-2xl font-bold text-white">{card.value}</p>
                    </div>
                ))}
            </div>

            {/* Founder Warehouse Trial Invitations Section */}
            <div
                data-testid="admin-warehouse-invitations-section"
                className="rounded-xl border border-white/10 bg-white/[0.02] p-5 space-y-5"
            >
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                    <div>
                        <h2 className="text-base font-bold text-white flex items-center gap-2">
                            <Mail size={16} className="text-indigo-400" />
                            Warehouse Trial Invitations
                        </h2>
                        <p className="text-xs text-slate-400 mt-0.5">
                            Generate a single-use, email-bound 30-day trial invitation, review recipient &amp; expiry, and explicitly send via Postmark.
                        </p>
                    </div>
                </div>

                {/* Applicant Selection + Generate Form */}
                <form
                    onSubmit={handleGenerateInvitation}
                    data-testid="admin-generate-invite-form"
                    className="grid grid-cols-1 md:grid-cols-12 gap-3 items-end"
                >
                    {trialRequests.length > 0 && (
                        <div className="md:col-span-12">
                            <label
                                htmlFor="admin-select-trial-applicant"
                                className="block text-[11px] text-slate-400 mb-1"
                            >
                                Select Recent Trial Request Applicant (optional)
                            </label>
                            <select
                                id="admin-select-trial-applicant"
                                data-testid="admin-select-trial-applicant"
                                defaultValue=""
                                onChange={(e) => handleSelectApplicant(e.target.value)}
                                className="w-full px-3 py-2 rounded-lg bg-[#141720] border border-white/10 text-xs text-white focus:outline-none focus:ring-1 focus:ring-indigo-500/40"
                            >
                                <option value="">— Choose from recent trial requests or enter email manually below —</option>
                                {trialRequests.map((req) => (
                                    <option key={req.id} value={req.id}>
                                        {req.companyName} — {req.contactName} ({req.email})
                                        {req.city ? ` · ${req.city}${req.province ? `, ${req.province}` : ''}` : ''}
                                        {req.dockDoors ? ` · ${req.dockDoors} docks` : ''}
                                    </option>
                                ))}
                            </select>
                        </div>
                    )}

                    <div className="md:col-span-4">
                        <label
                            htmlFor="admin-invite-email"
                            className="block text-[11px] text-slate-400 mb-1"
                        >
                            Approved Applicant Email <span className="text-red-400">*</span>
                        </label>
                        <input
                            id="admin-invite-email"
                            data-testid="admin-invite-email-input"
                            type="email"
                            required
                            value={inviteEmail}
                            onChange={(e) => setInviteEmail(e.target.value)}
                            placeholder="applicant@warehouse.com"
                            className="w-full px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-sm text-white placeholder-slate-500 focus:outline-none focus:ring-1 focus:ring-indigo-500/40"
                        />
                    </div>

                    <div className="md:col-span-4">
                        <label
                            htmlFor="admin-invite-company"
                            className="block text-[11px] text-slate-400 mb-1"
                        >
                            Company Name (optional)
                        </label>
                        <input
                            id="admin-invite-company"
                            data-testid="admin-invite-company-input"
                            type="text"
                            maxLength={120}
                            value={inviteCompany}
                            onChange={(e) => setInviteCompany(e.target.value)}
                            placeholder="Pacific Distribution Ltd."
                            className="w-full px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-sm text-white placeholder-slate-500 focus:outline-none focus:ring-1 focus:ring-indigo-500/40"
                        />
                    </div>

                    <div className="md:col-span-2">
                        <label
                            htmlFor="admin-invite-expiry"
                            className="block text-[11px] text-slate-400 mb-1"
                        >
                            Expires In
                        </label>
                        <select
                            id="admin-invite-expiry"
                            data-testid="admin-invite-expiry-select"
                            value={inviteExpiryDays}
                            onChange={(e) => setInviteExpiryDays(Number(e.target.value))}
                            className="w-full px-3 py-2 rounded-lg bg-[#141720] border border-white/10 text-sm text-white focus:outline-none focus:ring-1 focus:ring-indigo-500/40"
                        >
                            <option value={7}>7 days</option>
                            <option value={14}>14 days</option>
                            <option value={30}>30 days</option>
                        </select>
                    </div>

                    <div className="md:col-span-2">
                        <button
                            type="submit"
                            data-testid="admin-generate-invite-btn"
                            disabled={generatingInvite || !inviteEmail.trim()}
                            className="w-full px-3 py-2 rounded-lg text-xs font-semibold bg-indigo-500/20 text-indigo-200 border border-indigo-500/30 hover:bg-indigo-500/30 disabled:opacity-50 disabled:cursor-not-allowed transition-colors inline-flex items-center justify-center gap-1.5 h-[38px]"
                        >
                            <Plus size={14} />
                            {generatingInvite ? 'Generating…' : 'Generate Invite'}
                        </button>
                    </div>
                </form>

                {/* Review Generated Invitation & Explicit Postmark Send */}
                {generatedInvite && (
                    <div
                        data-testid="admin-invite-review-panel"
                        className="rounded-xl border border-indigo-500/30 bg-indigo-500/10 p-4 space-y-3"
                    >
                        <div className="flex flex-wrap items-center justify-between gap-2">
                            <div className="text-xs font-semibold text-indigo-200">
                                Review Single-Use Invitation Before Sending
                            </div>
                            <span
                                data-testid="admin-review-invite-status"
                                className="px-2 py-0.5 rounded-full text-[11px] font-semibold bg-amber-500/20 text-amber-300 border border-amber-500/30 uppercase"
                            >
                                {generatedInvite.invitation.status}
                            </span>
                        </div>

                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-xs">
                            <div>
                                <span className="text-slate-400 block">Recipient Email</span>
                                <span
                                    data-testid="admin-review-recipient"
                                    className="font-mono font-semibold text-white"
                                >
                                    {generatedInvite.invitation.invitedEmail}
                                </span>
                            </div>
                            <div>
                                <span className="text-slate-400 block">Company</span>
                                <span className="font-medium text-slate-200">
                                    {generatedInvite.invitation.companyName || '—'}
                                </span>
                            </div>
                            <div>
                                <span className="text-slate-400 block">Expires</span>
                                <span
                                    data-testid="admin-review-expiry"
                                    className="font-medium text-slate-200"
                                >
                                    {formatDate(generatedInvite.invitation.expiresAt)}
                                </span>
                            </div>
                        </div>

                        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 pt-1">
                            <input
                                type="text"
                                readOnly
                                data-testid="admin-review-invite-url"
                                value={generatedInvite.inviteUrl}
                                className="flex-1 px-3 py-1.5 rounded-lg bg-black/40 border border-white/10 text-xs font-mono text-slate-300"
                            />
                            <button
                                type="button"
                                data-testid="admin-copy-invite-url-btn"
                                onClick={() => {
                                    navigator.clipboard?.writeText(generatedInvite.inviteUrl);
                                    toast.success('Invitation link copied');
                                }}
                                className="px-3 py-2 rounded-lg text-xs font-semibold bg-white/10 hover:bg-white/15 text-slate-200 border border-white/10 inline-flex items-center justify-center gap-1.5"
                            >
                                <Copy size={13} />
                                Copy Link
                            </button>
                            <button
                                type="button"
                                data-testid="admin-send-invite-email-btn"
                                disabled={sendingInviteId === generatedInvite.invitation.id}
                                onClick={() =>
                                    handleSendInvitationEmail(
                                        generatedInvite.invitation.id,
                                        generatedInvite.inviteToken
                                    )
                                }
                                className="px-4 py-2 rounded-lg text-xs font-semibold bg-emerald-500/20 hover:bg-emerald-500/30 text-emerald-200 border border-emerald-500/30 disabled:opacity-50 inline-flex items-center justify-center gap-1.5"
                            >
                                <Send size={13} />
                                {sendingInviteId === generatedInvite.invitation.id
                                    ? 'Sending via Postmark…'
                                    : generatedInvite.emailSent
                                      ? 'Resend via Postmark'
                                      : 'Send Invitation via Postmark'}
                            </button>
                        </div>
                        {generatedInvite.emailSent && (
                            <p
                                data-testid="admin-invite-email-sent-confirmation"
                                className="text-xs text-emerald-300 font-medium"
                            >
                                ✓ Transactional invitation email sent via Postmark to {generatedInvite.invitation.invitedEmail}.
                            </p>
                        )}
                    </div>
                )}

                {/* Invitations List Table */}
                <div className="overflow-x-auto rounded-lg border border-white/5">
                    <table data-testid="admin-invitations-table" className="w-full text-xs">
                        <thead>
                            <tr className="border-b border-white/5 bg-white/[0.02]">
                                <th className="text-left px-3 py-2.5 text-[11px] text-slate-400 uppercase tracking-wider font-semibold">Recipient Email</th>
                                <th className="text-left px-3 py-2.5 text-[11px] text-slate-400 uppercase tracking-wider font-semibold">Company</th>
                                <th className="text-center px-3 py-2.5 text-[11px] text-slate-400 uppercase tracking-wider font-semibold">Status</th>
                                <th className="text-left px-3 py-2.5 text-[11px] text-slate-400 uppercase tracking-wider font-semibold">Created</th>
                                <th className="text-left px-3 py-2.5 text-[11px] text-slate-400 uppercase tracking-wider font-semibold">Expires</th>
                                <th className="text-right px-3 py-2.5 text-[11px] text-slate-400 uppercase tracking-wider font-semibold">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {invitationsLoading && invitations.length === 0 ? (
                                <tr>
                                    <td colSpan={6} className="px-3 py-6 text-center text-slate-500">
                                        Loading invitations…
                                    </td>
                                </tr>
                            ) : invitations.length === 0 ? (
                                <tr>
                                    <td colSpan={6} className="px-3 py-6 text-center text-slate-500">
                                        No warehouse invitations issued yet
                                    </td>
                                </tr>
                            ) : (
                                invitations.map((inv) => (
                                    <tr
                                        key={inv.id}
                                        data-testid={`admin-invitation-row-${inv.id}`}
                                        className="border-b border-white/[0.03] hover:bg-white/[0.02]"
                                    >
                                        <td className="px-3 py-2.5 font-mono text-slate-200">{inv.invitedEmail}</td>
                                        <td className="px-3 py-2.5 text-slate-300">{inv.companyName || '—'}</td>
                                        <td className="px-3 py-2.5 text-center">
                                            <span
                                                data-testid={`admin-invitation-status-${inv.id}`}
                                                className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase border ${
                                                    inv.status === 'pending'
                                                        ? 'bg-amber-500/15 text-amber-300 border-amber-500/25'
                                                        : inv.status === 'used'
                                                          ? 'bg-emerald-500/15 text-emerald-300 border-emerald-500/25'
                                                          : inv.status === 'revoked'
                                                            ? 'bg-red-500/15 text-red-300 border-red-500/25'
                                                            : 'bg-slate-500/15 text-slate-400 border-slate-500/25'
                                                }`}
                                            >
                                                {inv.status}
                                            </span>
                                        </td>
                                        <td className="px-3 py-2.5 text-slate-400">{formatDate(inv.createdAt)}</td>
                                        <td className="px-3 py-2.5 text-slate-400">{formatDate(inv.expiresAt)}</td>
                                        <td className="px-3 py-2.5 text-right">
                                            {inv.status === 'pending' ? (
                                                <div className="inline-flex items-center gap-1.5">
                                                    <button
                                                        type="button"
                                                        data-testid={`admin-send-row-invite-${inv.id}`}
                                                        disabled={sendingInviteId === inv.id}
                                                        onClick={() =>
                                                            handleSendInvitationEmail(
                                                                inv.id,
                                                                generatedInvite?.invitation.id === inv.id
                                                                    ? generatedInvite.inviteToken
                                                                    : undefined
                                                            )
                                                        }
                                                        className="px-2.5 py-1 rounded-md text-[11px] font-semibold bg-emerald-500/15 hover:bg-emerald-500/25 text-emerald-300 border border-emerald-500/25 inline-flex items-center gap-1"
                                                    >
                                                        <Send size={11} />
                                                        {sendingInviteId === inv.id ? 'Sending…' : 'Send via Postmark'}
                                                    </button>
                                                    <button
                                                        type="button"
                                                        data-testid={`admin-revoke-invite-${inv.id}`}
                                                        disabled={revokingInviteId === inv.id}
                                                        onClick={() => handleRevokeInvitation(inv.id)}
                                                        className="px-2.5 py-1 rounded-md text-[11px] font-semibold bg-red-500/15 hover:bg-red-500/25 text-red-300 border border-red-500/25 inline-flex items-center gap-1"
                                                    >
                                                        <Ban size={11} />
                                                        {revokingInviteId === inv.id ? '…' : 'Revoke'}
                                                    </button>
                                                </div>
                                            ) : (
                                                <span className="text-slate-600">—</span>
                                            )}
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Filters + Search */}
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
                <div className="flex gap-1 bg-white/5 rounded-lg p-1 border border-white/5">
                    {filterTabs.map(tab => (
                        <button
                            key={tab.key}
                            onClick={() => setFilter(tab.key)}
                            className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${filter === tab.key
                                ? 'bg-indigo-500/20 text-indigo-300 border border-indigo-500/30'
                                : 'text-slate-400 hover:text-white hover:bg-white/5 border border-transparent'
                                }`}
                        >
                            {tab.label} <span className="ml-1 opacity-60">({tab.count})</span>
                        </button>
                    ))}
                </div>
                <div className="relative flex-1 max-w-xs">
                    <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
                    <input
                        type="text"
                        value={search}
                        onChange={e => setSearch(e.target.value)}
                        placeholder="Search warehouse or email…"
                        className="w-full pl-9 pr-3 py-2 rounded-lg bg-white/5 border border-white/10 text-sm text-white placeholder-slate-500 focus:outline-none focus:ring-1 focus:ring-indigo-500/40 focus:border-indigo-500/30"
                    />
                </div>
            </div>

            {/* Table */}
            {loading ? (
                <div className="text-center py-16 text-slate-500">Loading accounts…</div>
            ) : error ? (
                <div className="text-center py-16 text-red-400">{error}</div>
            ) : filtered.length === 0 ? (
                <div className="text-center py-16 text-slate-500">
                    {search || filter !== 'all' ? 'No matching accounts' : 'No warehouse accounts found'}
                </div>
            ) : (
                <div className="overflow-x-auto rounded-xl border border-white/5">
                    <table className="w-full text-sm">
                        <thead>
                            <tr className="border-b border-white/5 bg-white/[0.02]">
                                <th className="text-left px-4 py-3 text-[11px] text-slate-400 uppercase tracking-wider font-semibold">Warehouse</th>
                                <th className="text-left px-4 py-3 text-[11px] text-slate-400 uppercase tracking-wider font-semibold">Owner</th>
                                <th className="text-left px-4 py-3 text-[11px] text-slate-400 uppercase tracking-wider font-semibold">Created</th>
                                <th className="text-left px-4 py-3 text-[11px] text-slate-400 uppercase tracking-wider font-semibold">Trial Start</th>
                                <th className="text-left px-4 py-3 text-[11px] text-slate-400 uppercase tracking-wider font-semibold">Trial End</th>
                                <th className="text-center px-4 py-3 text-[11px] text-slate-400 uppercase tracking-wider font-semibold">Days Left</th>
                                <th className="text-center px-4 py-3 text-[11px] text-slate-400 uppercase tracking-wider font-semibold">Status</th>
                                <th className="text-left px-4 py-3 text-[11px] text-slate-400 uppercase tracking-wider font-semibold">Plan</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filtered.map((w) => (
                                <tr
                                    key={w.warehouseId}
                                    onClick={() => openDetail(w.warehouseId)}
                                    className={`border-b border-white/[0.03] hover:bg-white/[0.04] transition-colors cursor-pointer ${selectedId === w.warehouseId ? 'bg-indigo-500/[0.06]' : ''}`}
                                >
                                    <td className="px-4 py-3">
                                        <div className="flex items-center gap-2">
                                            <Building2 size={14} className="text-slate-500 shrink-0" />
                                            <span className="text-white font-medium truncate max-w-[200px]">{w.warehouseName || '—'}</span>
                                        </div>
                                    </td>
                                    <td className="px-4 py-3 text-slate-300 truncate max-w-[200px]">{w.ownerEmail || '—'}</td>
                                    <td className="px-4 py-3 text-slate-400">{formatDate(w.createdAt)}</td>
                                    <td className="px-4 py-3 text-slate-400">{formatDate(w.trialStartAt)}</td>
                                    <td className="px-4 py-3 text-slate-400">{formatDate(w.trialEndAt)}</td>
                                    <td className="px-4 py-3 text-center">{daysLeftDisplay(w.trialDaysRemaining)}</td>
                                    <td className="px-4 py-3 text-center">{statusBadge(w.subscriptionStatus, w.trialDaysRemaining, w.isActive)}</td>
                                    <td className="px-4 py-3 text-slate-400 capitalize">{w.plan?.replace(/_/g, ' ') || '—'}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {/* Detail Drawer */}
            {selectedId && (
                <>
                    {/* Backdrop */}
                    <div
                        className="fixed inset-0 bg-black/40 z-40 backdrop-blur-sm"
                        onClick={closeDetail}
                    />
                    {/* Panel */}
                    <div className="fixed right-0 top-0 h-full w-full max-w-md bg-[#0f1117] border-l border-white/10 z-50 overflow-y-auto shadow-2xl">
                        {/* Header */}
                        <div className="flex items-center justify-between px-6 py-4 border-b border-white/5 sticky top-0 bg-[#0f1117]/95 backdrop-blur-md z-10">
                            <div className="flex items-center gap-2">
                                <Building2 size={18} className="text-indigo-400" />
                                <h2 className="text-lg font-bold text-white">Account Detail</h2>
                            </div>
                            <button onClick={closeDetail} className="p-1.5 rounded-lg text-slate-400 hover:text-white hover:bg-white/10 transition-colors">
                                <X size={18} />
                            </button>
                        </div>

                        {detailLoading ? (
                            <div className="text-center py-20 text-slate-500">Loading…</div>
                        ) : !detail ? (
                            <div className="text-center py-20 text-slate-500">Failed to load detail</div>
                        ) : (
                            <div className="p-6 space-y-5">
                                {/* Name + Status */}
                                <div>
                                    <h3 className="text-xl font-bold text-white mb-1">{detail.warehouseName}</h3>
                                    <div className="flex items-center gap-2">
                                        {statusBadge(detail.subscriptionStatus, detail.trialDaysRemaining, detail.isActive)}
                                        <span className="text-xs text-slate-500 capitalize">{detail.plan?.replace(/_/g, ' ') || '—'}</span>
                                    </div>
                                </div>

                                {/* Owner */}
                                <DetailSection title="Owner">
                                    <DetailRow icon={Mail} label="Email" value={detail.ownerEmail} />
                                    {detail.ownerName && <DetailRow icon={Shield} label="Name" value={detail.ownerName} />}
                                </DetailSection>

                                {/* Facility */}
                                <DetailSection title="Facility">
                                    <DetailRow icon={MapPin} label="Address" value={detail.address} />
                                    <DetailRow icon={Phone} label="Phone" value={detail.phone} />
                                    <DetailRow icon={Mail} label="Contact Email" value={detail.warehouseEmail} />
                                    <DetailRow icon={Globe} label="Time Zone" value={detail.timeZone} />
                                </DetailSection>

                                {/* Activity */}
                                <DetailSection title="Activity">
                                    <DetailRow icon={Anchor} label="Docks" value={String(detail.dockCount)} />
                                    <DetailRow icon={CalendarDays} label="Appointments" value={String(detail.appointmentCount)} />
                                    <DetailRow icon={Activity} label="Last Activity" value={detail.lastActivity ? formatDate(detail.lastActivity) : 'None'} />
                                </DetailSection>

                                {/* Trial */}
                                <DetailSection title="Trial & Subscription">
                                    <DetailRow icon={CalendarDays} label="Created" value={formatDate(detail.createdAt)} />
                                    <DetailRow icon={Clock} label="Trial Start" value={formatDate(detail.trialStartAt)} />
                                    <DetailRow icon={Clock} label="Trial End" value={formatDate(detail.trialEndAt)} />
                                    <DetailRow icon={Clock} label="Days Remaining" value={
                                        detail.trialDaysRemaining === null ? '—' :
                                            detail.trialDaysRemaining <= 0 ? 'Expired' :
                                                `${detail.trialDaysRemaining} days`
                                    } highlight={
                                        detail.trialDaysRemaining !== null && detail.trialDaysRemaining <= 5
                                            ? detail.trialDaysRemaining <= 0 ? 'red' : 'amber'
                                            : undefined
                                    } />
                                </DetailSection>

                                {/* Trial Controls */}
                                <div>
                                    <h4 className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold mb-2">Trial Controls</h4>
                                    <div className="flex flex-wrap gap-2">
                                        {[7, 14, 30].map(d => (
                                            <button
                                                key={d}
                                                onClick={() => extendTrial(d)}
                                                disabled={extending}
                                                className={`inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-semibold border transition-colors ${extending
                                                    ? 'bg-white/5 text-slate-500 border-white/5 cursor-not-allowed'
                                                    : 'bg-indigo-500/10 text-indigo-300 border-indigo-500/20 hover:bg-indigo-500/20 hover:border-indigo-500/30'
                                                    }`}
                                            >
                                                <Plus size={12} />
                                                {extending ? '…' : `+${d} Days`}
                                            </button>
                                        ))}
                                    </div>

                                    {/* Reset Trial */}
                                    <div className="mt-3 pt-3 border-t border-white/5">
                                        {!confirmReset ? (
                                            <button
                                                onClick={() => setConfirmReset(true)}
                                                disabled={resetting}
                                                className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-semibold border transition-colors bg-amber-500/10 text-amber-300 border-amber-500/20 hover:bg-amber-500/20"
                                            >
                                                <RotateCcw size={12} />
                                                Reset Trial
                                            </button>
                                        ) : (
                                            <div className="space-y-2">
                                                <p className="text-xs text-amber-300">This will start a brand-new 30-day trial from now, resetting plan to free_trial and status to trialing.</p>
                                                <div className="flex gap-2">
                                                    <button
                                                        onClick={resetTrial}
                                                        disabled={resetting}
                                                        className={`inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-semibold transition-colors ${resetting ? 'bg-white/5 text-slate-500 cursor-not-allowed' : 'bg-amber-500/20 text-amber-200 hover:bg-amber-500/30'
                                                            }`}
                                                    >
                                                        <RotateCcw size={12} />
                                                        {resetting ? 'Resetting…' : 'Yes, Reset Trial'}
                                                    </button>
                                                    <button
                                                        onClick={() => setConfirmReset(false)}
                                                        className="px-3 py-2 rounded-lg text-xs text-slate-400 hover:text-white hover:bg-white/5"
                                                    >
                                                        Cancel
                                                    </button>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>

                                {/* Subscription Controls */}
                                <div>
                                    <h4 className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold mb-2">Subscription Controls</h4>
                                    <div className="space-y-3 bg-white/[0.02] rounded-xl border border-white/5 p-3">
                                        <div>
                                            <label className="block text-[11px] text-slate-400 mb-1">Status</label>
                                            <select
                                                value={pendingStatus}
                                                onChange={e => setPendingStatus(e.target.value)}
                                                disabled={saving}
                                                className="w-full px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-sm text-white focus:outline-none focus:ring-1 focus:ring-indigo-500/40 disabled:opacity-50"
                                            >
                                                {STATUSES.map(s => (
                                                    <option key={s} value={s} className="bg-[#1a1b23]">{s.replace(/_/g, ' ')}</option>
                                                ))}
                                            </select>
                                        </div>
                                        <div>
                                            <label className="block text-[11px] text-slate-400 mb-1">Plan</label>
                                            <select
                                                value={pendingPlan}
                                                onChange={e => setPendingPlan(e.target.value)}
                                                disabled={saving}
                                                className="w-full px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-sm text-white focus:outline-none focus:ring-1 focus:ring-indigo-500/40 disabled:opacity-50"
                                            >
                                                {PLANS.map(p => (
                                                    <option key={p} value={p} className="bg-[#1a1b23]">{p.replace(/_/g, ' ')}</option>
                                                ))}
                                            </select>
                                        </div>
                                        <button
                                            onClick={updateSubscription}
                                            disabled={saving}
                                            className={`w-full py-2 rounded-lg text-xs font-semibold transition-colors ${saving
                                                ? 'bg-white/5 text-slate-500 cursor-not-allowed'
                                                : 'bg-indigo-500/15 text-indigo-300 border border-indigo-500/20 hover:bg-indigo-500/25'
                                                }`}
                                        >
                                            {saving ? 'Saving…' : 'Save Changes'}
                                        </button>
                                    </div>
                                </div>

                                {/* Access Controls */}
                                <div>
                                    <h4 className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold mb-2">Access Controls</h4>
                                    <div className="bg-white/[0.02] rounded-xl border border-white/5 p-3">
                                        {detail.isActive !== false ? (
                                            <div>
                                                {!confirmDeactivate ? (
                                                    <button
                                                        onClick={() => setConfirmDeactivate(true)}
                                                        disabled={toggling}
                                                        className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-semibold border transition-colors bg-red-500/10 text-red-400 border-red-500/20 hover:bg-red-500/20"
                                                    >
                                                        <ShieldOff size={12} />
                                                        Deactivate Access
                                                    </button>
                                                ) : (
                                                    <div className="space-y-2">
                                                        <p className="text-xs text-red-400">This will block all warehouse users from accessing the app. Are you sure?</p>
                                                        <div className="flex gap-2">
                                                            <button
                                                                onClick={() => toggleAccess(false)}
                                                                disabled={toggling}
                                                                className={`inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-semibold transition-colors ${toggling ? 'bg-white/5 text-slate-500 cursor-not-allowed' : 'bg-red-500/20 text-red-300 hover:bg-red-500/30'
                                                                    }`}
                                                            >
                                                                {toggling ? 'Deactivating…' : 'Yes, Deactivate'}
                                                            </button>
                                                            <button
                                                                onClick={() => setConfirmDeactivate(false)}
                                                                className="px-3 py-2 rounded-lg text-xs text-slate-400 hover:text-white hover:bg-white/5"
                                                            >
                                                                Cancel
                                                            </button>
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        ) : (
                                            <div className="space-y-2">
                                                <p className="text-xs text-red-400 flex items-center gap-1.5"><ShieldOff size={12} /> Account is currently deactivated</p>
                                                <button
                                                    onClick={() => toggleAccess(true)}
                                                    disabled={toggling}
                                                    className={`inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-semibold border transition-colors ${toggling ? 'bg-white/5 text-slate-500 cursor-not-allowed' : 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20 hover:bg-emerald-500/20'
                                                        }`}
                                                >
                                                    <ShieldCheck size={12} />
                                                    {toggling ? 'Reactivating…' : 'Reactivate Access'}
                                                </button>
                                            </div>
                                        )}
                                    </div>
                                </div>

                                {/* Onboarding Complete */}
                                <div>
                                    <h4 className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold mb-2">Onboarding</h4>
                                    <button
                                        onClick={markOnboardingComplete}
                                        disabled={markingOnboarding}
                                        className={`w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl text-xs font-semibold border transition-colors ${markingOnboarding
                                                ? 'bg-white/5 text-slate-500 border-white/5 cursor-not-allowed'
                                                : 'bg-emerald-500/10 text-emerald-300 border-emerald-500/20 hover:bg-emerald-500/20'
                                            }`}
                                    >
                                        <Rocket size={14} />
                                        {markingOnboarding ? 'Processing…' : 'Mark Onboarding Complete'}
                                    </button>
                                    <p className="text-[10px] text-slate-500 mt-1.5">Adds note, extends trial to 60 days if needed, logs audit event.</p>
                                </div>

                                {/* Internal Notes */}
                                <div>
                                    <h4 className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold mb-2">Internal Notes</h4>
                                    <div className="space-y-2 bg-white/[0.02] rounded-xl border border-white/5 p-3">
                                        <textarea
                                            value={noteText}
                                            onChange={e => setNoteText(e.target.value)}
                                            placeholder="Add an internal note…"
                                            maxLength={1000}
                                            rows={2}
                                            disabled={addingNote}
                                            className="w-full px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-sm text-white placeholder-slate-500 focus:outline-none focus:ring-1 focus:ring-indigo-500/40 resize-none disabled:opacity-50"
                                        />
                                        <div className="flex items-center justify-between">
                                            <span className="text-[10px] text-slate-600">{noteText.length}/1000</span>
                                            <button
                                                onClick={addNote}
                                                disabled={addingNote || !noteText.trim()}
                                                className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors ${addingNote || !noteText.trim()
                                                    ? 'bg-white/5 text-slate-500 cursor-not-allowed'
                                                    : 'bg-indigo-500/10 text-indigo-300 border border-indigo-500/20 hover:bg-indigo-500/20'
                                                    }`}
                                            >
                                                <MessageSquare size={12} />
                                                {addingNote ? 'Saving…' : 'Add Note'}
                                            </button>
                                        </div>
                                    </div>
                                </div>

                                {/* Admin Timeline */}
                                <div>
                                    <h4 className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold mb-2">Admin Timeline</h4>
                                    {auditHistory.length === 0 ? (
                                        <div className="text-center py-6 text-sm text-slate-500 bg-white/[0.02] rounded-xl border border-white/5">
                                            No admin actions recorded yet
                                        </div>
                                    ) : (
                                        <div className="space-y-0 bg-white/[0.02] rounded-xl border border-white/5 divide-y divide-white/5 max-h-64 overflow-y-auto">
                                            {auditHistory.map(entry => (
                                                <div key={entry.id} className="px-3 py-2.5">
                                                    <div className="flex items-start gap-2">
                                                        <History size={12} className="text-indigo-400 mt-0.5 shrink-0" />
                                                        <div className="flex-1 min-w-0">
                                                            <div className="flex items-center justify-between gap-2">
                                                                <span className="text-xs font-semibold text-slate-200">
                                                                    {formatAction(entry.action)}
                                                                </span>
                                                                <span className="text-[10px] text-slate-500 shrink-0">
                                                                    {entry.createdAt ? timeAgo(entry.createdAt) : '—'}
                                                                </span>
                                                            </div>
                                                            {(entry.oldValue || entry.newValue) && (
                                                                <p className="text-[11px] text-slate-400 mt-0.5">
                                                                    {entry.oldValue && <span className="text-red-400/70 line-through">{entry.oldValue}</span>}
                                                                    {entry.oldValue && entry.newValue && <span className="mx-1">→</span>}
                                                                    {entry.newValue && <span className="text-emerald-400/80">{entry.newValue}</span>}
                                                                </p>
                                                            )}
                                                            {entry.reason && (
                                                                <p className="text-[10px] text-slate-500 mt-0.5 italic">{entry.reason}</p>
                                                            )}
                                                        </div>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}
                    </div>
                </>
            )}
        </div>
    );
}

/* ---------- Sub-components ---------- */

function DetailSection({ title, children }: { title: string; children: React.ReactNode }) {
    return (
        <div>
            <h4 className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold mb-2">{title}</h4>
            <div className="space-y-1.5 bg-white/[0.02] rounded-xl border border-white/5 p-3">
                {children}
            </div>
        </div>
    );
}

function DetailRow({ icon: Icon, label, value, highlight }: {
    icon: any; label: string; value: string | null | undefined; highlight?: 'red' | 'amber';
}) {
    return (
        <div className="flex items-center gap-2.5 py-1">
            <Icon size={13} className="text-slate-500 shrink-0" />
            <span className="text-xs text-slate-400 w-28 shrink-0">{label}</span>
            <span className={`text-sm font-medium ${highlight === 'red' ? 'text-red-400' :
                highlight === 'amber' ? 'text-amber-400' :
                    'text-slate-200'
                }`}>{value || '—'}</span>
        </div>
    );
}

function formatAction(action: string): string {
    const map: Record<string, string> = {
        extend_trial: 'Trial Extended',
        update_subscription_status: 'Status Changed',
        update_plan: 'Plan Changed',
        admin_note: 'Note',
        deactivate_access: 'Access Deactivated',
        reactivate_access: 'Access Reactivated',
        trial_reset: 'Trial Reset',
        onboarding_complete: 'Onboarding Complete',
    };
    return map[action] || action.replace(/_/g, ' ');
}

function timeAgo(iso: string): string {
    const diff = Date.now() - new Date(iso).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return 'just now';
    if (mins < 60) return `${mins}m ago`;
    const hours = Math.floor(mins / 60);
    if (hours < 24) return `${hours}h ago`;
    const days = Math.floor(hours / 24);
    if (days < 30) return `${days}d ago`;
    return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}
